import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, formatApiError } from "../../lib/api";

import RichTextEditor from "../research/components/RichTextEditor";
import { ArrowLeft, Save, Trash2, Upload, X } from "lucide-react";

const EMPTY = {
  title: "", slug: "", subtitle: "", excerpt: "",
  body_html: "", category: "findings", industry: "",
  tags: [], featured: false, status: "draft", publish_at: "",
  cover_image_data_url: "", pdf_attachment_data_url: "", pdf_attachment_filename: "",
  platforms_tested: [], finding_summary: "", business_implication: "",
  company_a: "", company_b: "", score_a: null, score_b: null,
  recognition_a: "", recognition_b: "", category_a: "", category_b: "",
  competitive_a: "", competitive_b: "", key_takeaway: "",
  executive_summary: "", key_findings: [], related_reports: [],
  initial_score: null, final_score: null, findings: "", actions: "", outcome: "",
  seo: { meta_title: "", meta_description: "" },
};

const CATEGORIES = [
  { value: "findings", label: "Finding" },
  { value: "teardowns", label: "Teardown" },
  { value: "reports", label: "Industry Report" },
  { value: "case-studies", label: "Case Study" },
  { value: "methodology", label: "Methodology" },
  { value: "resources", label: "Resource" },
];

const MAX_BYTES = 8 * 1024 * 1024;

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (file.size > MAX_BYTES) return reject(new Error(`Files must be smaller than ${MAX_BYTES / (1024 * 1024)} MB`));
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function AdminPublicationForm() {
  const { pubId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(pubId);

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/publications/${pubId}`);
        setForm({ ...EMPTY, ...data, seo: data.seo || EMPTY.seo });
      } catch (e) {
        setErr(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [pubId, isEdit]);

  const update = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value ?? e;
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const updateNum = (k) => (e) => {
    const v = e.target.value === "" ? null : Number(e.target.value);
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const updateList = (k) => (e) => {
    const arr = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
    setForm((prev) => ({ ...prev, [k]: arr }));
  };

  const updateNewlineList = (k) => (e) => {
    const arr = e.target.value.split("\n").map((s) => s.trim()).filter(Boolean);
    setForm((prev) => ({ ...prev, [k]: arr }));
  };

  const onUpload = (k, withName) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      setForm((prev) => withName
        ? { ...prev, [k]: dataUrl, pdf_attachment_filename: file.name }
        : { ...prev, [k]: dataUrl });
    } catch (e) {
      setErr(e.message);
    }
  };

  const onSubmit = async (publishNow = false) => {
    setSaving(true);
    setErr("");
    try {
      const payload = { ...form };
      if (publishNow) payload.status = "published";
      // The Pydantic model rejects unset fields with default validators only,
      // so strip empties to keep payload clean
      const out = isEdit
        ? await api.patch(`/admin/publications/${pubId}`, payload)
        : await api.post("/admin/publications", payload);
      navigate("/admin/publications");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080807] text-[#f4efe6]">
  
        <main className="max-w-5xl mx-auto px-6 sm:px-8 py-24"><p className="text-[#8a7e6d]">Loading…</p></main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080807] text-[#f4efe6]" data-testid="admin-publication-form">
      <Helmet><title>{isEdit ? "Edit publication" : "New publication"}</title></Helmet>

      <main className="max-w-5xl mx-auto px-6 sm:px-8 py-12">
        <Link to="/admin/publications" className="inline-flex items-center gap-1 text-sm text-[#b8963e] hover:underline">
          <ArrowLeft className="w-4 h-4" /> All publications
        </Link>
        <h1 className="mt-4 font-serif-display text-3xl text-[#f4efe6]">
          {isEdit ? "Edit publication" : "New publication"}
        </h1>

        {err && (
          <div className="mt-4 p-3 border border-[#c46a5a]/40 bg-[#c46a5a]/10 text-sm text-[#e9a394] rounded-sm"
               data-testid="admin-pub-form-error">{err}</div>
        )}

        <form className="mt-8 space-y-6" onSubmit={(e) => { e.preventDefault(); onSubmit(false); }}>
          {/* Core fields */}
          <Field label="Title">
            <input value={form.title} onChange={update("title")} required
                   data-testid="admin-pub-title" className="eav-input w-full" placeholder="The full publication title" />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Category">
              <select value={form.category} onChange={update("category")} data-testid="admin-pub-category"
                      className="eav-input w-full">
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Industry">
              <input value={form.industry} onChange={update("industry")} data-testid="admin-pub-industry"
                     className="eav-input w-full" placeholder="Construction, SaaS, Healthcare…" />
            </Field>
          </div>

          <Field label="Subtitle"><input value={form.subtitle} onChange={update("subtitle")} className="eav-input w-full" data-testid="admin-pub-subtitle" /></Field>
          <Field label="Excerpt (used in cards & list views)">
            <textarea value={form.excerpt} onChange={update("excerpt")} rows={3} className="eav-input w-full" data-testid="admin-pub-excerpt" />
          </Field>

          {/* Cover image */}
          <Field label="Cover image">
            <CoverUploader value={form.cover_image_data_url}
                            onChange={(v) => setForm((p) => ({ ...p, cover_image_data_url: v }))}
                            onUpload={onUpload("cover_image_data_url", false)} />
          </Field>

          {/* Body */}
          <Field label="Body">
            <RichTextEditor value={form.body_html} onChange={(html) => setForm((p) => ({ ...p, body_html: html }))} />
          </Field>

          {/* Category-specific fields */}
          {form.category === "findings" && (
            <div className="space-y-4 p-5 border border-[#2a2620] rounded-sm bg-[#0c0a08]" data-testid="admin-form-findings">
              <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#b8963e]">Finding fields</p>
              <Field label="Platforms tested (comma-separated)">
                <input value={(form.platforms_tested || []).join(", ")}
                       onChange={updateList("platforms_tested")}
                       data-testid="admin-pub-platforms"
                       className="eav-input w-full" placeholder="ChatGPT, Claude, Gemini, Perplexity" />
              </Field>
              <Field label="Finding summary"><textarea value={form.finding_summary} onChange={update("finding_summary")} rows={4} className="eav-input w-full" /></Field>
              <Field label="Business implication"><textarea value={form.business_implication} onChange={update("business_implication")} rows={4} className="eav-input w-full" /></Field>
            </div>
          )}

          {form.category === "teardowns" && (
            <div className="space-y-4 p-5 border border-[#2a2620] rounded-sm bg-[#0c0a08]" data-testid="admin-form-teardowns">
              <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#b8963e]">Teardown fields</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Company A"><input value={form.company_a} onChange={update("company_a")} className="eav-input w-full" data-testid="admin-pub-company-a" /></Field>
                <Field label="Company B"><input value={form.company_b} onChange={update("company_b")} className="eav-input w-full" data-testid="admin-pub-company-b" /></Field>
                <Field label="Score A (0-100)"><input type="number" min={0} max={100} value={form.score_a ?? ""} onChange={updateNum("score_a")} className="eav-input w-full" /></Field>
                <Field label="Score B (0-100)"><input type="number" min={0} max={100} value={form.score_b ?? ""} onChange={updateNum("score_b")} className="eav-input w-full" /></Field>
                <Field label="Recognition — A"><textarea value={form.recognition_a} onChange={update("recognition_a")} rows={3} className="eav-input w-full" /></Field>
                <Field label="Recognition — B"><textarea value={form.recognition_b} onChange={update("recognition_b")} rows={3} className="eav-input w-full" /></Field>
                <Field label="Category Inclusion — A"><textarea value={form.category_a} onChange={update("category_a")} rows={3} className="eav-input w-full" /></Field>
                <Field label="Category Inclusion — B"><textarea value={form.category_b} onChange={update("category_b")} rows={3} className="eav-input w-full" /></Field>
                <Field label="Competitive Association — A"><textarea value={form.competitive_a} onChange={update("competitive_a")} rows={3} className="eav-input w-full" /></Field>
                <Field label="Competitive Association — B"><textarea value={form.competitive_b} onChange={update("competitive_b")} rows={3} className="eav-input w-full" /></Field>
              </div>
              <Field label="Key takeaway"><textarea value={form.key_takeaway} onChange={update("key_takeaway")} rows={3} className="eav-input w-full" /></Field>
            </div>
          )}

          {form.category === "reports" && (
            <div className="space-y-4 p-5 border border-[#2a2620] rounded-sm bg-[#0c0a08]" data-testid="admin-form-reports">
              <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#b8963e]">Industry report fields</p>
              <Field label="Executive summary"><textarea value={form.executive_summary} onChange={update("executive_summary")} rows={5} className="eav-input w-full" /></Field>
              <Field label="Key findings (one per line)">
                <textarea value={(form.key_findings || []).join("\n")} onChange={updateNewlineList("key_findings")} rows={6} className="eav-input w-full" placeholder="One key finding per line…" data-testid="admin-pub-key-findings" />
              </Field>
              <Field label="Related reports (slugs, comma-separated)">
                <input value={(form.related_reports || []).join(", ")} onChange={updateList("related_reports")} className="eav-input w-full" />
              </Field>
              <Field label="Downloadable PDF (≤ 8 MB)">
                <PdfUploader value={form.pdf_attachment_data_url}
                              filename={form.pdf_attachment_filename}
                              onChange={(v) => setForm((p) => ({ ...p, pdf_attachment_data_url: v }))}
                              onUpload={onUpload("pdf_attachment_data_url", true)}
                              onClear={() => setForm((p) => ({ ...p, pdf_attachment_data_url: "", pdf_attachment_filename: "" }))} />
              </Field>
            </div>
          )}

          {form.category === "case-studies" && (
            <div className="space-y-4 p-5 border border-[#2a2620] rounded-sm bg-[#0c0a08]" data-testid="admin-form-case-studies">
              <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#b8963e]">Case study fields</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Initial score (0-100)"><input type="number" min={0} max={100} value={form.initial_score ?? ""} onChange={updateNum("initial_score")} className="eav-input w-full" data-testid="admin-pub-initial-score" /></Field>
                <Field label="Final score (0-100)"><input type="number" min={0} max={100} value={form.final_score ?? ""} onChange={updateNum("final_score")} className="eav-input w-full" data-testid="admin-pub-final-score" /></Field>
              </div>
              <Field label="Findings"><textarea value={form.findings} onChange={update("findings")} rows={4} className="eav-input w-full" /></Field>
              <Field label="Recommended actions"><textarea value={form.actions} onChange={update("actions")} rows={4} className="eav-input w-full" /></Field>
              <Field label="Outcome"><textarea value={form.outcome} onChange={update("outcome")} rows={4} className="eav-input w-full" /></Field>
            </div>
          )}

          {/* Tags */}
          <Field label="Tags (comma-separated)">
            <input value={(form.tags || []).join(", ")} onChange={updateList("tags")} className="eav-input w-full" data-testid="admin-pub-tags" />
          </Field>

          {/* SEO */}
          <div className="space-y-4 p-5 border border-[#2a2620] rounded-sm bg-[#0c0a08]">
            <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#b8963e]">SEO</p>
            <Field label="Meta title">
              <input value={form.seo?.meta_title || ""}
                     onChange={(e) => setForm((p) => ({ ...p, seo: { ...p.seo, meta_title: e.target.value } }))}
                     className="eav-input w-full" data-testid="admin-pub-meta-title" />
            </Field>
            <Field label="Meta description">
              <textarea value={form.seo?.meta_description || ""}
                        onChange={(e) => setForm((p) => ({ ...p, seo: { ...p.seo, meta_description: e.target.value } }))}
                        rows={3} className="eav-input w-full" data-testid="admin-pub-meta-description" />
            </Field>
            <Field label="Custom slug (optional)">
              <input value={form.slug} onChange={update("slug")} className="eav-input w-full" placeholder="auto-generated from title if blank" />
            </Field>
          </div>

          {/* Publish controls */}
          <div className="grid sm:grid-cols-3 gap-4 p-5 border border-[#2a2620] rounded-sm bg-[#0c0a08]">
            <Field label="Status">
              <select value={form.status} onChange={update("status")} className="eav-input w-full" data-testid="admin-pub-status">
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="published">Published</option>
              </select>
            </Field>
            <Field label="Publish date">
              <input type="datetime-local"
                     value={form.publish_at ? form.publish_at.slice(0, 16) : ""}
                     onChange={(e) => setForm((p) => ({ ...p, publish_at: e.target.value ? new Date(e.target.value).toISOString() : "" }))}
                     className="eav-input w-full" data-testid="admin-pub-publish-at" />
            </Field>
            <Field label="Featured">
              <label className="inline-flex items-center gap-2 mt-3 cursor-pointer">
                <input type="checkbox" checked={!!form.featured} onChange={update("featured")} className="accent-[#b8963e]" data-testid="admin-pub-featured" />
                <span className="text-sm text-[#c9bca4]">Mark as featured</span>
              </label>
            </Field>
          </div>

          <div className="flex flex-wrap gap-3 pt-4 border-t border-[#2a2620]">
            <button type="submit" disabled={saving}
                    data-testid="admin-pub-save"
                    className="btn-gold text-sm">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
            </button>
            <button type="button" onClick={() => onSubmit(true)} disabled={saving}
                    data-testid="admin-pub-save-publish"
                    className="px-4 py-2 border border-[#b8963e] text-[#b8963e] hover:bg-[#b8963e] hover:text-[#0c0a08] transition-colors font-mono-eav text-xs tracking-[0.18em] uppercase rounded-sm">
              Save &amp; publish
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d]">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function CoverUploader({ value, onChange, onUpload }) {
  return (
    <div className="flex items-start gap-4">
      <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-[#2a2620] hover:border-[#b8963e] rounded-sm text-sm text-[#c9bca4]"
             data-testid="admin-pub-cover-upload">
        <Upload className="w-4 h-4" /> Upload cover
        <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
      </label>
      {value && (
        <div className="relative">
          <img src={value} alt="cover preview" className="h-20 w-32 object-cover rounded-sm border border-[#2a2620]" />
          <button type="button" onClick={() => onChange("")}
                  className="absolute -top-2 -right-2 p-1 bg-[#0c0a08] rounded-full border border-[#2a2620] text-[#c46a5a] hover:bg-[#c46a5a] hover:text-white">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}

function PdfUploader({ value, filename, onUpload, onClear }) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-[#2a2620] hover:border-[#b8963e] rounded-sm text-sm text-[#c9bca4]"
             data-testid="admin-pub-pdf-upload">
        <Upload className="w-4 h-4" /> Upload PDF
        <input type="file" accept="application/pdf" className="hidden" onChange={onUpload} />
      </label>
      {value && (
        <>
          <span className="text-xs text-[#c9bca4] font-mono-eav">{filename || "attached.pdf"}</span>
          <button type="button" onClick={onClear} className="text-xs text-[#c46a5a] hover:underline">
            <Trash2 className="w-3 h-3 inline" /> Remove
          </button>
        </>
      )}
    </div>
  );
}
